"use client";

/* eslint-disable import/no-extraneous-dependencies */
/* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
import React, {
  useCallback,
  useEffect,
  useRef,
  createRef,
  useMemo,
  createContext,
  useContext,
} from "react";

import { useSession, getSession } from "next-auth/react";
export { useSession, SessionProvider } from "next-auth/react";
import { type LogoutOptions, type RedirectLoginOptions } from "@auth0/auth0-react";

import pick from "lodash/pick";

import useStadiumSharedGlobalConfigContext from "@stadium/shared/src/hooks/use-global-context";

import { BYSTADIUM_DASHBOARD_HOSTNAME } from "@stadium/helpers/app-routes";
import { OPEN_ORDER_PREFIX_KEY, USER_DEPENDENT_PREFIX_KEY } from "@stadium/constants";

import {
  CHECK_AUTHENTICATED_SHOPS_ROUTE,
  LOGIN_ROUTE,
  LOGOUT_ROUTE,
  REFRESH_TOKEN_ERROR,
  isDev,
} from "@stadium/services/auth/constants";
import { checkIfTokenValid } from "@stadium/services/auth/utils";

import {
  ESHOPS,
  STORES,
  SUPERIORDISHES,
  SWAGMAGIC,
  SNACKMAGIC,
} from "@stadium/shared/src/constants/brands";

const whiteListedAuth0Params = [
  "signUp",
  "customEmail",
  "brand",
  "theme",
  "pwdMessage",
  "pwdSuccess",
  "section",
  "firstName",
  "lastName",
  "role",
  "email",
  "layout",
  "originUrl",
];

const siteBrandMapping = {
  [ESHOPS]: "eshops",
  [STORES]: "eshops",
  [SUPERIORDISHES]: "dsd",
  [SWAGMAGIC]: "SwagMagic",
  [SNACKMAGIC]: "snackmagic",
};

export const AuthContext = createContext({
  axiosInterceptorAdded: false,
  graphqlInterceptorAdded: false,
});

export const useAuthContext = () => useContext(AuthContext);

export const logoutRef = createRef();

export const getAuthUrl = ({
  callbackUrl = window.location.href,
  site,
  baseRoute,
  loginOptions = {},
}: {
  callbackUrl?: string;
  site: string;
  baseRoute: string;
  loginOptions?: { [key: string]: string };
}) => {
  const isShopsSite = site === ESHOPS;
  const isStoresSite = site === STORES;
  const authUrl = new URL(
    `${isShopsSite ? BYSTADIUM_DASHBOARD_HOSTNAME : window.location.origin}${
      isStoresSite ? "/stores" : ""
    }${baseRoute}`,
  );

  Object.entries(loginOptions).forEach(([key, value]) => {
    if (key === "theme") {
      authUrl.searchParams.set("layout", value);
    } else if (key === "customEmail") {
      authUrl.searchParams.set("email", value);
    } else {
      authUrl.searchParams.set(key, value);
    }
  });

  authUrl.searchParams.set("returnTo", callbackUrl);

  return authUrl.toString();
};

const getLoginUrl = (props: Omit<Parameters<typeof getAuthUrl>[0], "baseRoute">) =>
  getAuthUrl({ baseRoute: LOGIN_ROUTE, ...props });

const getLogoutUrl = (props: Omit<Parameters<typeof getAuthUrl>[0], "baseRoute" | "signUp">) =>
  getAuthUrl({ baseRoute: LOGOUT_ROUTE, ...props });

const initiateAuth = (() => {
  let _initiatedAuth = false;
  return async (site, isAuthenticated, userEmail) => {
    if (_initiatedAuth) return;

    _initiatedAuth = true;
    const res = await fetch(`${BYSTADIUM_DASHBOARD_HOSTNAME}${CHECK_AUTHENTICATED_SHOPS_ROUTE}`, {
      credentials: "include",
    });
    const response = await res.json();

    const { isAuthenticated: isAuthenticatedInShops, email } = response || {};

    if (isAuthenticatedInShops && (!isAuthenticated || email !== userEmail)) {
      const loginUrl = getLoginUrl({ site });
      window.location.href = loginUrl;
    }
  };
})();

const getPrevSessionToken = () =>
  new Promise((res: (token: string) => void) => {
    window.addEventListener("authTokenProcessing", (e: CustomEvent) => res(e.detail));
  });

export const useAuth = () => {
  const { data, status, update } = useSession();
  const { site } = useStadiumSharedGlobalConfigContext();
  const brand = siteBrandMapping[site];

  const isShopsSite = site === ESHOPS;

  const isLoading = !data && status === "loading";
  const isAuthenticated = Boolean(data) && !data.user?.error;

  const { name, email, email_verified, picture, sub } = data?.user || {};

  const user = useMemo(() => {
    if (data?.user) {
      return {
        name,
        email,
        email_verified,
        picture,
        sub,
      };
    }
    return null;
  }, [name, email, email_verified, picture, sub]);

  useEffect(() => {
    if (isLoading || isDev) return;
    initiateAuth(site, isAuthenticated, email);
  }, [isLoading, isAuthenticated, email, isDev]);

  const authDataRef = useRef({ data, isAuthenticated, update, fetchSessionCount: 0 });
  authDataRef.current = {
    data,
    isAuthenticated,
    update,
    fetchSessionCount: authDataRef.current.fetchSessionCount,
  };

  const removeAllOpenOrderKeys = useCallback(() => {
    try {
      Object.keys(localStorage)
        .filter(
          (x) => x.startsWith(OPEN_ORDER_PREFIX_KEY) || x.startsWith(USER_DEPENDENT_PREFIX_KEY),
        )
        .forEach((x) => localStorage.removeItem(x));
    } catch (error) {}
  }, []);

  const cleanupUserDependencies = useCallback(() => {
    removeAllOpenOrderKeys();
  }, [removeAllOpenOrderKeys]);

  const logout = useCallback(({ returnTo, logoutParams }: LogoutOptions = {}) => {
    const callbackUrl = returnTo || logoutParams?.returnTo;

    cleanupUserDependencies();
    if (isShopsSite || isDev) {
      const logoutUrl = getLogoutUrl({ callbackUrl, site });
      window.location.href = logoutUrl;
      return;
    }

    const returnToUrl = getLogoutUrl({ callbackUrl, site });
    const logoutUrl = getLogoutUrl({
      callbackUrl: returnToUrl,
      // --using dashboard logout route as base route for logout--//
      site: ESHOPS,
    });

    window.location.href = logoutUrl;
  }, []);

  const getAccessTokenSilently = useCallback(async () => {
    const { isAuthenticated, data, update } = authDataRef.current;
    if (!isAuthenticated) return null;

    const {
      access_token: accessToken,
      expires_at: accessTokenExpiry,
      refresh_token: refreshToken,
    } = data?.user || {};

    if (checkIfTokenValid(accessTokenExpiry)) return accessToken;

    if (!refreshToken) {
      logout();
      return null;
    }

    authDataRef.current.fetchSessionCount += 1;

    if (authDataRef.current.fetchSessionCount > 1) {
      const token = await getPrevSessionToken();
      authDataRef.current.fetchSessionCount -= 1;
      return token;
    }

    let session = await getSession({ broadcast: false });
    if (session?.user?.error === REFRESH_TOKEN_ERROR) {
      // retrying one more time to get the access token
      session = await getSession({ broadcast: false });
      if (session?.user?.error === REFRESH_TOKEN_ERROR) {
        logout();
        return null;
      }
    }

    const token = session?.user?.access_token;
    const authTokenProcessingEvent = new CustomEvent("authTokenProcessing", { detail: token });
    window.dispatchEvent(authTokenProcessingEvent);
    authDataRef.current.fetchSessionCount -= 1;

    await update();
    return token;
  }, []);

  const loginWithRedirect = useCallback(
    ({ appState, ...restLoginOptions }: RedirectLoginOptions = {}) => {
      const callbackUrl = appState?.returnTo || "/";

      const loginOptions = pick(restLoginOptions, whiteListedAuth0Params);

      if (isShopsSite || isDev) {
        const loginUrl = getLoginUrl({
          callbackUrl,
          site,
          loginOptions: { ...loginOptions, brand },
        });
        window.location.href = loginUrl;
        return;
      }

      const returnToUrl = getLoginUrl({ callbackUrl, site });
      const loginUrl = getLoginUrl({
        callbackUrl: returnToUrl,
        // --using dashboard login route as base route for login--//
        site: ESHOPS,
        loginOptions: { ...loginOptions, brand },
      });
      window.location.href = loginUrl;
    },
    [],
  );

  const loginWithPopup = useCallback(
    ({ appState, ...restLoginOptions }: RedirectLoginOptions = {}) => {
      const callbackUrl = appState?.returnTo || "/login-callback";
      const loginOptions = pick(restLoginOptions, whiteListedAuth0Params);

      let loginUrl;
      if (isShopsSite || isDev) {
        loginUrl = getLoginUrl({
          callbackUrl,
          site,
          loginOptions: { ...loginOptions, brand },
        });
      } else {
        const returnToUrl = getLoginUrl({ callbackUrl, site });
        loginUrl = getLoginUrl({
          callbackUrl: returnToUrl,
          site: ESHOPS,
          loginOptions: { ...loginOptions, brand },
        });
      }

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        loginUrl,
        "Login",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`,
      );

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        const { loggedIn } = event.data;
        if (loggedIn) {
          popup.close();
          window.removeEventListener("message", handleMessage);
        }
      };
      window.addEventListener("message", handleMessage);
    },
    [site, isShopsSite, isDev, brand],
  );

  return {
    loginWithRedirect,
    loginWithPopup,
    getAccessTokenSilently,
    logout,
    user,
    isAuthenticated,
    isLoading,
    updateSession: update,
    sessionData: data,
  };
};

export const withAuth = (Component) => {
  const UpdatedComponent = (props) => {
    const auth0 = useAuth();
    return <Component {...props} auth0={auth0} />;
  };
  return UpdatedComponent;
};

export const useIsAuthGraphQLReady = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { graphqlInterceptorAdded } = useAuthContext();

  return isAuthenticated && graphqlInterceptorAdded && !isLoading;
};

export const useIsAuthAxiosReady = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { axiosInterceptorAdded } = useAuthContext();

  return isAuthenticated && axiosInterceptorAdded && !isLoading;
};
